import React, { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/services/api';

import { CustomerList } from './crm/CustomerList';
import { FollowUpList } from './crm/FollowUpList';
import { CustomerDetailModal } from './crm/CustomerDetailModal';
import { AddCustomerModal } from './crm/AddCustomerModal';
import { FollowUpModal } from './crm/FollowUpModal';

const EMPTY_CUSTOMER = { name: '', email: '', mobile: '', primary_address: '', lifecycle_stage: 'lead', tags: [], marketing_opt_in: false };
const EMPTY_FOLLOWUP = { customer_id: '', title: '', description: '', due_date: '', priority: 'medium' };

const CRMPanel = () => {
  const [customers, setCustomers] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('customers');
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [newCustomer, setNewCustomer] = useState(EMPTY_CUSTOMER);
  const [newFollowUp, setNewFollowUp] = useState(EMPTY_FOLLOWUP);
  const [newNote, setNewNote] = useState('');

  const fetchCustomers = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (stageFilter !== 'all') params.append('lifecycle_stage', stageFilter);
      const response = await api.get(`/api/admin/customers?${params}`);
      if (response.data.success) setCustomers(response.data.customers);
    } catch { setCustomers([]); }
    finally { setIsLoading(false); }
  }, [search, stageFilter]);

  const fetchFollowUps = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/follow-ups?completed=false');
      if (response.data.success) setFollowUps(response.data.follow_ups);
    } catch { setFollowUps([]); }
  }, []);

  useEffect(() => { fetchCustomers(); fetchFollowUps(); }, [fetchCustomers, fetchFollowUps]);

  const handleViewCustomer = async (customer) => {
    try {
      const response = await api.get(`/api/admin/customers/${customer.customer_id}`);
      if (response.data.success) {
        setSelectedCustomer({ ...response.data.customer, orders: response.data.orders, follow_ups: response.data.follow_ups });
        setShowCustomerModal(true);
      }
    } catch {
      setSelectedCustomer(customer);
      setShowCustomerModal(true);
    }
  };

  const handleAddCustomer = async () => {
    if (!newCustomer.name || !newCustomer.mobile) { toast.error('Name and mobile are required'); return; }
    setIsSaving(true);
    try {
      const response = await api.post('/api/admin/customers', newCustomer);
      if (response.data.success) {
        toast.success(response.data.existing ? 'Customer already exists' : 'Customer created');
        setShowAddModal(false);
        fetchCustomers();
        setNewCustomer(EMPTY_CUSTOMER);
      }
    } catch { toast.error('Failed to create customer'); }
    finally { setIsSaving(false); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) { toast.error('Please enter a note'); return; }
    try {
      await api.post(`/api/admin/customers/${selectedCustomer.customer_id}/notes`, { content: newNote });
      toast.success('Note added');
      setNewNote('');
      handleViewCustomer(selectedCustomer);
    } catch { toast.error('Failed to add note'); }
  };

  const handleCreateFollowUp = async () => {
    if (!newFollowUp.title || !newFollowUp.due_date) { toast.error('Title and due date are required'); return; }
    setIsSaving(true);
    try {
      await api.post('/api/admin/follow-ups', { ...newFollowUp, customer_id: selectedCustomer?.customer_id || newFollowUp.customer_id });
      toast.success('Follow-up created');
      setShowFollowUpModal(false);
      fetchFollowUps();
      setNewFollowUp(EMPTY_FOLLOWUP);
    } catch { toast.error('Failed to create follow-up'); }
    finally { setIsSaving(false); }
  };

  const handleCompleteFollowUp = async (followUp) => {
    try {
      await api.put(`/api/admin/follow-ups/${followUp.follow_up_id}/complete`);
      toast.success('Follow-up completed');
      fetchFollowUps();
    } catch { toast.error('Failed to complete follow-up'); }
  };

  return (
    <div className="space-y-6" data-testid="crm-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#001A47]">Customer Relationship Management</h2>
          <p className="text-gray-500">Manage customers, follow-ups, and engagement</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowFollowUpModal(true)}>
            <Clock className="h-4 w-4 mr-2" />New Follow-Up
          </Button>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4 mr-2" />Add Customer
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="customers"><Users className="h-4 w-4 mr-2" />Customers ({customers.length})</TabsTrigger>
          <TabsTrigger value="follow-ups"><Clock className="h-4 w-4 mr-2" />Follow-Ups ({followUps.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="customers" className="mt-6">
          <CustomerList
            customers={customers} isLoading={isLoading}
            search={search} setSearch={setSearch}
            stageFilter={stageFilter} setStageFilter={setStageFilter}
            onViewCustomer={handleViewCustomer}
            onAddCustomer={() => setShowAddModal(true)}
          />
        </TabsContent>

        <TabsContent value="follow-ups" className="mt-6">
          <FollowUpList followUps={followUps} onComplete={handleCompleteFollowUp} onCreateNew={() => setShowFollowUpModal(true)} />
        </TabsContent>
      </Tabs>

      <CustomerDetailModal
        open={showCustomerModal} onOpenChange={setShowCustomerModal}
        customer={selectedCustomer} newNote={newNote} setNewNote={setNewNote} onAddNote={handleAddNote}
      />
      <AddCustomerModal
        open={showAddModal} onOpenChange={setShowAddModal}
        customer={newCustomer} setCustomer={setNewCustomer} onSave={handleAddCustomer} isSaving={isSaving}
      />
      <FollowUpModal
        open={showFollowUpModal} onOpenChange={setShowFollowUpModal}
        followUp={newFollowUp} setFollowUp={setNewFollowUp} onSave={handleCreateFollowUp} isSaving={isSaving}
      />
    </div>
  );
};

export default CRMPanel;
